import React, { useState, useEffect, useRef, useContext } from 'react';
import { RectangleHorizontal, Ban } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import CesiumContext from './../../context/CesiumContext';
import PolygonDrawer from './../../helper/PolygonDrawer';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export type DrawMode = 'EDIT' | 'INSERT' | 'NONE' | 'CANCEL';

interface DamageDrawToolProps {
	onDrawComplete?: (positions: any[], type: 'damage' | 'inspection') => void;
}

export default function DamageDrawTool({ onDrawComplete }: DamageDrawToolProps) {
	const { t } = useTranslation();
	const { viewer, drawMode, setDrawMode, setSelectedDamageEntity, setSelectedDamage } = useContext(
		CesiumContext
	) as {
		viewer: any;
		drawMode: DrawMode;
		setDrawMode: (mode: DrawMode) => void;
		setSelectedDamageEntity: (entity: any) => void;
		setSelectedDamage: (damage: any) => void;
	};

	const [active, setActive] = useState<boolean>(false);
	const drawerRef = useRef<any>(null);

	const { inspectionId, damageId } = useParams<{
		inspectionId?: string;
		damageId?: string;
	}>();

	const saveType = damageId ? 'damage' : inspectionId ? 'inspection' : null;

	// Disabled if:
	// 1. Global mode is EDIT.
	// 2. Global mode is INSERT but THIS tool is not the one active.
	const isDisabled = drawMode === 'EDIT' || (drawMode === 'INSERT' && !active);

	// Handle Start/Stop based on local 'active' state
	useEffect(() => {
		if (!viewer) return;

		if (active) {
			startDrawing();
		} else {
			stopDrawing();
		}

		return () => stopDrawing();
	}, [active, viewer]);

	// Listen for Global Cancel or External Changes
	useEffect(() => {
		if (active && drawMode !== 'INSERT') {
			setActive(false);
			stopDrawing();
		}

		if (drawMode === 'CANCEL') {
			stopDrawing();
			setActive(false);
		}
	}, [drawMode]);

	const handleButtonClick = () => {
		if (!saveType) {
			toast.error(t('damages.draw.openInspectionRequired'));
			return;
		}
		// Toggle state
		if (active) setDrawMode('CANCEL');
		else setActive(true);
	};

	const startDrawing = () => {
		setDrawMode('INSERT');
		if (!drawerRef.current) {
			drawerRef.current = new PolygonDrawer(viewer, {
				callback: (result: any) => {
					console.log('Polygon Drawn:', result, 'Type:', saveType);
					if (onDrawComplete && saveType) {
						onDrawComplete(result, saveType);
					}
					// Ideally, we keep the tool active or reset it.
					// For polygons, usually we stop after one.
					// setActive(false); // Uncomment if you want auto-close
				},
			});
		}
		drawerRef.current.start();
	};

	const stopDrawing = () => {
		if (drawerRef.current) {
			drawerRef.current.clear();
			drawerRef.current.stop();
			drawerRef.current = null;
		}

		setSelectedDamageEntity(null);
		setSelectedDamage(null);
		setDrawMode('NONE');
	};

	return (
		<button
			onClick={handleButtonClick}
			disabled={isDisabled}
			title={t('damages.draw.markAreaDamage')}
			className={cn(
				'flex items-center gap-1.5 px-2 sm:px-3 h-8 rounded-md text-xs font-medium transition-all duration-200 whitespace-nowrap',
				active
					? 'bg-primary text-primary-foreground shadow-sm hover:opacity-90'
					: isDisabled
						? 'opacity-50 cursor-not-allowed text-foreground/40 bg-transparent'
						: 'text-foreground/80 hover:bg-accent hover:text-accent-foreground'
			)}
		>
			{active ? <Ban className="w-3.5 h-3.5 shrink-0" /> : <RectangleHorizontal className="w-3.5 h-3.5 shrink-0" />}
			<span className="hidden sm:inline">{active ? t('common.actions.cancel') : t('damages.draw.markDamage')}</span>
		</button>
	);
}
